import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router";

/**
 * Put the reader at the top of the page they just navigated to.
 *
 * The bug this fixes is the footer's. The footer is at the bottom of every
 * page, some of which are very long — Terms is thousands of words — so by
 * definition you click "Privacy" from the bottom of the viewport. A client-side
 * router swaps the component and leaves the scroll position exactly where it
 * was, so Privacy opens scrolled to somewhere in its middle. It reads as a
 * broken link or a page that failed to load, and the reader has no reason to
 * suspect they need to scroll UP.
 *
 * Three cases, and they want different answers:
 *
 *   1. A hash (`/why-kev#the-idea`, and every in-page anchor the marketing
 *      page uses). Scroll to that element, not to the top. Without this
 *      branch the "See the idea" CTA in the hero would jump to the top of
 *      the page it is already at the top of, and do visibly nothing.
 *
 *   2. POP — the back and forward buttons. Leave it alone. The browser restores
 *      the previous scroll position itself and that is the correct behaviour;
 *      forcing the top here would mean going Back from a legal page dumped you
 *      at the top of the landing page instead of at the link you left from.
 *
 *   3. Everything else (PUSH/REPLACE) — top of the page.
 *
 * Scrolls the WINDOW only. The chat pane manages its own scroll and is not a
 * document-level scroller, so this never touches it.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP") return;

    if (hash) {
      /*
       * The target may not be mounted yet on a fresh navigation — the route's
       * component renders in the same commit this effect runs after, but a
       * lazy chunk or a query-driven section may not be there. Fall through to
       * the top rather than leaving the reader wherever they happened to be.
       */
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth" });
        return;
      }
    }

    /* Instant, not smooth. A smooth scroll on a route change animates the NEW
       page sliding past content the reader never asked to see, and on a long
       document it takes long enough to look like a bug. */
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash, navigationType]);

  return null;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
